import { LoadingButton } from "@mui/lab";
import {
  Alert,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useSignalReady } from "contexts/globalLoadingScreen";
import { usePromptDialog } from "contexts/promptDialog";
import { FirebaseError } from "firebase/app";
import {
  getAuth,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { backendApi } from "src/store/api/enhancedApi";

export const SignInCompletePage = () => {
  useSignalReady();

  const { t } = useTranslation();
  const auth = getAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authProvider = searchParams.get("authProvider") ?? "microsoft";
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [linkExpired, setLinkExpired] = useState(false);
  const [linkResent, setLinkResent] = useState(false);
  const [expiredEmail, setExpiredEmail] = useState<string | null>(null);
  const prompt = usePromptDialog();
  const [requestSignInLink, { isLoading: resendingLink }] =
    backendApi.endpoints.requestSignInLink.useMutation();

  const completeSignIn = useCallback(async () => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      setError(t("invalidSignInLink"));
      setIsProcessing(false);
      return;
    }

    let email = localStorage.getItem("emailForSignIn");
    if (email) {
      localStorage.removeItem("emailForSignIn");
    } else {
      const emailFromUrl = searchParams.get("email");
      email = await prompt({
        title: t("enterYourEmail"),
        message: t("enterEmailToCompleteSignIn"),
        label: t("email"),
        defaultValue: emailFromUrl ?? undefined,
      });
    }

    if (!email) {
      setError(t("signInCancelled"));
      setIsProcessing(false);
      return;
    }

    try {
      await signInWithEmailLink(auth, email, window.location.href);
      localStorage.removeItem("tenantForSignIn");
      localStorage.setItem("authProvider", authProvider);
      void navigate("/link-account");
    } catch (err) {
      if (
        err instanceof FirebaseError &&
        (err.code === "auth/expired-action-code" ||
          err.code === "auth/invalid-action-code")
      ) {
        setExpiredEmail(email);
        setLinkExpired(true);
        setIsProcessing(false);
      } else {
        setError(t("failedToSignIn"));
        setIsProcessing(false);
      }
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- searchParams is a new URLSearchParams object on every render; stale closure is fine since completeSignIn only runs once on mount
  }, [auth, navigate, authProvider, t, prompt]);

  useEffect(() => {
    void completeSignIn();
  }, [completeSignIn]);

  const handleResendLink = async () => {
    if (!expiredEmail) return;
    const tenant =
      searchParams.get("tenantName") ?? localStorage.getItem("tenantForSignIn");
    if (!tenant) {
      setError(t("signInLinkSendFailed"));
      return;
    }
    localStorage.setItem("emailForSignIn", expiredEmail);
    try {
      await requestSignInLink({
        tenantName: tenant,
        requestSignInLinkRequest: { email: expiredEmail },
      }).unwrap();
      setLinkResent(true);
      setLinkExpired(false);
    } catch {
      setError(t("signInLinkSendFailed"));
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        height: "100%",
        px: { xs: 2, sm: 0 },
        display: "flex",
        alignItems: "center",
      }}
    >
      <Paper sx={{ width: "100%" }} elevation={2}>
        <Stack
          direction="column"
          justifyContent="center"
          alignItems="center"
          sx={{ px: { xs: 3, sm: 8 }, pt: 4, pb: 4 }}
          spacing={3}
        >
          <Typography variant="h4">{t("scrapChef")}</Typography>
          {isProcessing ? (
            <>
              <CircularProgress />
              <Typography>{t("completingSignIn")}</Typography>
            </>
          ) : linkExpired ? (
            <>
              <Alert severity="warning" sx={{ width: "100%" }}>
                {t("signInLinkExpired")}
              </Alert>
              <LoadingButton
                loading={resendingLink}
                variant="outlined"
                sx={{ textTransform: "none", borderRadius: 0 }}
                onClick={() => void handleResendLink()}
              >
                {t("resendSignInLink")}
              </LoadingButton>
            </>
          ) : linkResent ? (
            <Alert severity="success" sx={{ width: "100%" }}>
              {t("signInLinkResent")}
            </Alert>
          ) : error ? (
            <Alert severity="error" sx={{ width: "100%" }}>
              {error}
            </Alert>
          ) : null}
        </Stack>
      </Paper>
    </Container>
  );
};
